import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { compact } from 'lodash-es';
import { ActiveServicesReportEntryApiOccurrence } from 'anchor-scout-common/types';
import { Button, FilterPickerGroup, Page, PageContentHeader, Spinner, TableList } from 'seagull';
import RestfulWrapper from '~/components/RestfulWrapper/RestfulWrapper';
import TableFilters from '~/components/TableFilters/TableFilters';
import { VisibleColumnsPicker } from '~/components/VisibleColumnsPicker';
import { routes } from '~/constants';
import { useIoContext, useStore, useStoreActions } from '~/contexts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useOnMount } from '~/hooks/useOnMount';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useSortFromUrl } from '~/hooks/useSortFromUrl';
import { useLocaleKeys } from '~/i18n';
import { selectors } from '~/store/state-selectors';
import { formatLongMonthUSDate } from '~/utils';
import { ActiveServiceRow } from './ActiveServiceRow';
import styles from './ActiveServices.module.scss';
import { activeServicesReportColumnDefs, activeServicesReportHeaderColumnDefs, activeServicesReportHeaderColumnDefsForExport, activeServicesReportTextsPath, getFilterLabels, getDefaultSort, makeCsv, makeFiltersConfig } from './ActiveServices.utils';
import { ActiveServicesReportOptionalColumns, FilterType } from './types';
const testIdPrefix = 'active-services-report';
export const ActiveServicesReport = ()=>{
    const history = useHistory();
    const [activeServicesReportData, setActiveServicesReportData] = useState([]);
    const [selectedFilters, setSelectedFilters] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const { browser: { downloadCsv } } = useIoContext();
    const { ON_DEMAND } = useFeatureFlags();
    const { t: reportTexts } = useLocaleKeys(activeServicesReportTextsPath);
    const { t: reportsTexts } = useLocaleKeys('pages.reports');
    const title = reportsTexts.activeServicesTitle();
    const [visibleColumns, setVisibleColumns] = useState({
        [ActiveServicesReportOptionalColumns.CustomerName]: true,
        [ActiveServicesReportOptionalColumns.ServiceName]: true,
        [ActiveServicesReportOptionalColumns.Trigger]: false,
        [ActiveServicesReportOptionalColumns.Pricing]: false,
        [ActiveServicesReportOptionalColumns.Occurrence]: true,
        [ActiveServicesReportOptionalColumns.Rate]: true,
        [ActiveServicesReportOptionalColumns.LastBilled]: false,
        [ActiveServicesReportOptionalColumns.Assignee]: false,
        [ActiveServicesReportOptionalColumns.AutomaticServiceState]: false,
        [ActiveServicesReportOptionalColumns.Status]: true
    });
    const headerItems = useMemo(()=>activeServicesReportHeaderColumnDefs({
            visibleColumns,
            texts: reportTexts.labels
        }), [
        visibleColumns
    ]);
    const sortIds = compact(headerItems.map((item)=>item.sortId));
    const defaultSort = useMemo(()=>getDefaultSort(sortIds), [
        sortIds
    ]);
    const { sort, changeSort } = useSortFromUrl({
        defaultSort,
        sortIds
    });
    const { api } = useStoreActions();
    const businessId = useStore(selectors.user.businessId);
    const businessName = useStore(selectors.user.businessName);
    const handleFetchFilterConfig = async ()=>{
        const [serviceNames, customerNames, agreementNames, assignees] = await Promise.all([
            api.reports.activeServicesReportServices(),
            api.reports.activeServicesReportCustomers(),
            api.reports.activeServicesReportAgreements(),
            api.reports.activeServicesReportAssignees()
        ]);
        const filterOptions = {
            [FilterType.ServiceName]: serviceNames,
            [FilterType.CustomerName]: customerNames,
            [FilterType.AgreementName]: agreementNames,
            [FilterType.Assignee]: assignees
        };
        return makeFiltersConfig({
            filtersOptions: filterOptions,
            onDemandEnabled: !!ON_DEMAND,
            texts: reportTexts
        });
    };
    const updateVisibleColumns = (columns)=>{
        setVisibleColumns(columns);
        if (sort) {
            const currentSortId = sortIds[sort.colIndex];
            const visibleKeys = Object.keys(columns).filter((key)=>!!columns[key]);
            const visibleSortIds = headerItems.filter((item)=>!item.optionalColumn || visibleKeys.includes(item.optionalColumn)).map((item)=>item.sortId);
            if (!visibleSortIds.includes(currentSortId)) {
                changeSort(getDefaultSort(visibleSortIds));
            }
        }
    };
    const fetchActiveServicesReport = (selectedFilters, sort)=>{
        var _selectedFilters_FilterType_LastBilled, _selectedFilters_FilterType_LastBilled1;
        if (businessId == null) return;
        setIsLoading(true);
        api.reports.activeServicesReport({
            businessId,
            customers: selectedFilters[FilterType.CustomerName] || [],
            services: selectedFilters[FilterType.ServiceName] || [],
            agreements: selectedFilters[FilterType.AgreementName] || [],
            assignees: selectedFilters[FilterType.Assignee] || [],
            params: {
                sort: sort ? [
                    sortIds[sort.colIndex],
                    sort.sort
                ] : undefined,
                occurrence: selectedFilters[FilterType.Occurrence],
                trigger: selectedFilters[FilterType.Trigger],
                pricing: selectedFilters[FilterType.Pricing],
                automaticServiceState: selectedFilters[FilterType.AutomaticServiceState],
                status: selectedFilters[FilterType.Status],
                lastBilledSince: (_selectedFilters_FilterType_LastBilled = selectedFilters[FilterType.LastBilled]) === null || _selectedFilters_FilterType_LastBilled === void 0 ? void 0 : _selectedFilters_FilterType_LastBilled.from,
                lastBilledUntil: (_selectedFilters_FilterType_LastBilled1 = selectedFilters[FilterType.LastBilled]) === null || _selectedFilters_FilterType_LastBilled1 === void 0 ? void 0 : _selectedFilters_FilterType_LastBilled1.to
            }
        }).then((data)=>setActiveServicesReportData(data)).finally(()=>setIsLoading(false));
    };
    useSetDocumentTitle(title);
    useOnMount(()=>{
        fetchActiveServicesReport(selectedFilters, sort);
    });
    const onExport = ()=>downloadCsv(makeCsv(activeServicesReportHeaderColumnDefsForExport({
            visibleColumns,
            texts: reportTexts.labels
        }).map((param)=>{
            let { value } = param;
            return value;
        }), visibleColumns, activeServicesReportData), "".concat(businessName, "_active_services_report_").concat(formatLongMonthUSDate(new Date()), ".csv"));
    const onSortChange = (sort)=>{
        changeSort(sort);
        fetchActiveServicesReport(selectedFilters, sort);
    };
    return /*#__PURE__*/ _jsx(Page, {
        testId: testIdPrefix,
        header: title,
        variant: "containedHeight",
        onClickBack: ()=>history.push(routes.reports),
        actions: /*#__PURE__*/ _jsx(Button, {
            bi: {
                name: 'export_active_services_report'
            },
            skin: "primary",
            testId: "".concat(testIdPrefix, "-export-btn"),
            onClick: onExport,
            children: "Export"
        }),
        children: /*#__PURE__*/ _jsxs(TableList.Provider, {
            columnDefs: activeServicesReportColumnDefs(visibleColumns),
            numRows: activeServicesReportData.length,
            sort: sort,
            onSortChange: onSortChange,
            children: [
                /*#__PURE__*/ _jsx(PageContentHeader, {
                    children: /*#__PURE__*/ _jsx("div", {
                        className: styles.pageContent,
                        children: /*#__PURE__*/ _jsxs(FilterPickerGroup, {
                            flexWrap: true,
                            children: [
                                /*#__PURE__*/ _jsx(VisibleColumnsPicker, {
                                    testId: testIdPrefix,
                                    visibleColumns: visibleColumns,
                                    onApply: updateVisibleColumns,
                                    fixedColumns: {
                                        [ActiveServicesReportOptionalColumns.ServiceName]: true,
                                        [ActiveServicesReportOptionalColumns.CustomerName]: true
                                    }
                                }),
                                /*#__PURE__*/ _jsx(RestfulWrapper, {
                                    fetch: handleFetchFilterConfig,
                                    renderLoader: ()=>/*#__PURE__*/ _jsx(Spinner, {
                                            size: 24
                                        }),
                                    renderChild: (param)=>{
                                        let { data: filterConfig } = param;
                                        return /*#__PURE__*/ _jsx(TableFilters, {
                                            testIdPrefix: testIdPrefix,
                                            filterLabels: getFilterLabels(reportTexts.labels),
                                            filterConfig: filterConfig,
                                            selectedFilters: selectedFilters,
                                            setSelectedFilters: setSelectedFilters,
                                            onRefresh: fetchActiveServicesReport
                                        });
                                    }
                                })
                            ]
                        })
                    })
                }),
                /*#__PURE__*/ _jsx(TableList.Structure, {
                    header: /*#__PURE__*/ _jsx(TableList.Header, {
                        stickyToTop: true,
                        items: headerItems,
                        testId: "".concat(testIdPrefix, "-table-header")
                    }),
                    content: isLoading ? /*#__PURE__*/ _jsx(Spinner, {
                        centeredInBox: true
                    }) : /*#__PURE__*/ _jsx(_Fragment, {
                        children: activeServicesReportData.filter((service)=>ON_DEMAND || service.occurrence !== ActiveServicesReportEntryApiOccurrence.OnDemand).map((activeService, index)=>/*#__PURE__*/ _jsx(ActiveServiceRow, {
                                index: index,
                                data: activeService,
                                visibleColumns: visibleColumns
                            }, "".concat(activeService.customerName, "-").concat(activeService.serviceName, "-").concat(index)))
                    })
                })
            ]
        })
    });
};
